/**
 * Resolving a project's document, and adapting a legacy project into one.
 *
 * A project used to BE one kind — video, voice or text — which meant a shoot that
 * wanted a take, a photo and a note was three projects. Now a project holds
 * SECTIONS of ELEMENTS, and each element is one of those kinds.
 *
 * ## The migration, and why it writes nothing
 *
 * Every project on every existing phone predates sections. None of them are
 * backfilled. `resolveDocument` synthesises a document from what a legacy record
 * already has — its `kind`, its clips, its text body — and that synthesis is
 * deterministic, so the same project resolves the same way every time it is
 * opened.
 *
 * That is the same choice made for `Project.kind` (absent reads as video) and
 * `ProjectAudioTrack.startMs` (absent reads as sequential), and for the same
 * reason: a backfill is a write over someone's footage, run once, at the worst
 * possible moment — app start, on a phone, possibly mid-upload — and a half-
 * finished one leaves a project that is neither shape. Reading costs a few
 * microseconds per open and cannot corrupt anything.
 *
 * A project becomes stored-document-shaped the first time it is actually EDITED
 * as one, which is a write the user asked for.
 */
import {
  DEFAULT_PICTURE_HOLD_MS,
  defaultLayoutFor,
  newId,
  projectKind,
  type ClipId,
  type ClipRecord,
  type ElementId,
  type ElementKind,
  type Project,
  type ProjectDocument,
  type ProjectElement,
  type ProjectSection,
  type ProjectTextRecord,
} from './types'

/** What the first section is called when nobody names it. */
export const DEFAULT_SECTION_NAME = 'Section 1'

/** A clip's media kind, defaulting records written before pictures existed. */
export function clipMediaKind(clip: Pick<ClipRecord, 'mediaKind'>): 'video' | 'picture' {
  return clip.mediaKind === 'picture' ? 'picture' : 'video'
}

/**
 * The element kind a legacy project's clips become.
 *
 * A VOICE project's clips become nothing at all — see `synthesiseDocument`. They
 * are mic-only takes, and there is no element kind for sound with no picture.
 */
function legacyElementKind(_project: Pick<Project, 'kind'>): ElementKind {
  return 'video'
}

export interface ResolveDocumentInput {
  project: Pick<Project, 'id' | 'kind'>
  clips: readonly ClipRecord[]
  /** The project's stored body, if any. */
  text?: ProjectTextRecord | null
  /** Stored sections and elements. Empty means "legacy, synthesise one". */
  sections?: readonly ProjectSection[]
  elements?: readonly ProjectElement[]
}

/**
 * The project's document: stored if it has one, synthesised if it does not.
 *
 * Synthesis is faithful rather than clever. A legacy video project is a sequence
 * of camera takes, so it becomes one element PER CLIP in clip order — not one
 * element holding every clip, which would turn a five-cut film into a single
 * five-way collage. A text project becomes one text element carrying its body.
 *
 * A video or voice project's text body is NOT turned into an element: there it
 * means "notes published alongside", which is still what it means. Only a TEXT
 * project's body is the content itself.
 */
export function resolveDocument(input: ResolveDocumentInput): ProjectDocument {
  const stored = input.sections ?? []
  if (stored.length > 0) {
    return {
      sections: [...stored],
      elements: [...(input.elements ?? [])],
    }
  }
  return synthesiseDocument(input)
}

/** True when this project has never been saved in the new shape. */
export function isLegacyProject(sections: readonly ProjectSection[] | undefined): boolean {
  return !sections || sections.length === 0
}

/**
 * Build a document for a project that has never had one.
 *
 * Ids are DERIVED from the project and clip ids rather than random, so a legacy
 * project resolves to the same document on every open. Random ids would make an
 * element's identity change under the UI between renders — a selection that
 * silently jumps, a reorder that reorders something else.
 */
export function synthesiseDocument(input: ResolveDocumentInput): ProjectDocument {
  const projectId = input.project.id
  const sectionId = `sec-legacy-${projectId}`
  const now = 0

  const section: ProjectSection = {
    id: sectionId,
    projectId,
    name: DEFAULT_SECTION_NAME,
    sort: 0,
    createdAt: now,
  }

  const kind = legacyElementKind(input.project)
  const elements: ProjectElement[] = []

  // A legacy VOICE project's clips are mic-only takes, and there is no element
  // kind for sound with no picture — so its picture is genuinely empty. Nothing
  // is lost by saying so: a voice project has its own screen and its own export,
  // neither of which reads this document, and turning its takes into video
  // elements would put a stretch of black in a film that never had one.
  const takesArePicture = projectKind(input.project) !== 'voice'

  // Clip order is the film's order. `clips` arrives already ordered by the
  // caller (it resolves the project's own clipIds), so it is not re-sorted here
  // — re-sorting by createdAt would reorder a film someone had rearranged.
  if (takesArePicture) {
    input.clips.forEach((clip, index) => {
      elements.push({
        id: `el-legacy-${clip.id}`,
        projectId,
        sectionId,
        kind: clipMediaKind(clip) === 'picture' ? 'picture' : kind,
        sort: index,
        clipIds: [clip.id],
        layout: 'single',
        createdAt: clip.createdAt,
        updatedAt: clip.createdAt,
      })
    })
  }

  // Only a TEXT project's body is content. Elsewhere it is notes.
  const body = input.text?.body?.trim()
  if (projectKind(input.project) === 'text' && body) {
    elements.push({
      id: `el-legacy-text-${projectId}`,
      projectId,
      sectionId,
      kind: 'text',
      sort: elements.length,
      clipIds: [],
      contentMd: input.text!.body,
      createdAt: input.text!.updatedAt,
      updatedAt: input.text!.updatedAt,
    })
  }

  return { sections: [section], elements }
}

/* ── durations ───────────────────────────────────────────────────────────── */

/** A clip's kept length. A picture's is zero — its file has no length. */
export function clipKeptMs(
  clip: Pick<ClipRecord, 'durationMs' | 'trimStartMs' | 'trimEndMs' | 'mediaKind'>,
): number {
  if (clipMediaKind(clip) === 'picture') return 0
  const end = Math.max(0, Math.min(clip.trimEndMs, clip.durationMs))
  const start = Math.max(0, Math.min(clip.trimStartMs, end))
  return Math.max(0, end - start)
}

/**
 * How long an element occupies the film.
 *
 * The rules differ by kind because the sources do:
 *
 *  - **video** is as long as its media. A collage of clips is as long as its
 *    LONGEST clip, not their sum — they play together, side by side.
 *  - **voice** is as long as its audio, and may be padded by an explicit
 *    duration to hold its card past the end of the sound.
 *  - **picture** and **text** have no inherent length, so they hold for their
 *    own duration, then the project's, then the built-in default.
 *  - **collage** is as long as its LONGEST item, for the same reason a
 *    multi-clip video element is: its cells play at once. An explicit duration
 *    on a collage is EXACT rather than a floor — set it shorter and the cells
 *    are cut off there, set it longer and each holds its last frame for the
 *    remainder. That is the one kind whose stated length can shorten what is
 *    inside it, and this is why: the collage is the scene, and its items are
 *    material inside the scene rather than the scene itself.
 */
export function elementDurationMs(
  element: Pick<ProjectElement, 'kind' | 'clipIds' | 'durationMs' | 'itemElementIds'>,
  clipsById: ReadonlyMap<ClipId, ClipRecord>,
  projectHoldMs?: number,
  /** The project's elements. Needed only to measure a `collage`. */
  elementsById?: ReadonlyMap<ElementId, ProjectElement>,
): number {
  const media = element.clipIds
    .map((id) => clipsById.get(id))
    .filter((clip): clip is ClipRecord => clip !== undefined)

  if (element.kind === 'collage') {
    const stated = element.durationMs
    if (stated !== undefined && Number.isFinite(stated) && stated > 0) {
      return Math.round(stated)
    }
    // Items are never collages, so this cannot recurse further.
    const longest = collageItems(element, elementsById).reduce(
      (max, item) => Math.max(max, elementDurationMs(item, clipsById, projectHoldMs)),
      0,
    )
    if (longest > 0) return longest
    // An empty collage still has to hold for something, or it is a zero-length
    // beat the plan drops without ever saying why.
    return Math.round(
      projectHoldMs !== undefined && Number.isFinite(projectHoldMs) && projectHoldMs > 0
        ? projectHoldMs
        : DEFAULT_PICTURE_HOLD_MS,
    )
  }

  if (element.kind === 'video') {
    // Longest, not sum: the items in a collage are concurrent.
    const longest = media.reduce((max, clip) => Math.max(max, clipKeptMs(clip)), 0)
    const explicit = element.durationMs
    if (explicit !== undefined && Number.isFinite(explicit) && explicit > 0) {
      // An explicit duration only ever EXTENDS media — a voice element cannot be
      // shortened this way, because cutting audio is a trim on the clip.
      return Math.max(longest, Math.round(explicit))
    }
    return longest
  }

  const hold =
    element.durationMs !== undefined && Number.isFinite(element.durationMs) && element.durationMs > 0
      ? element.durationMs
      : projectHoldMs !== undefined && Number.isFinite(projectHoldMs) && projectHoldMs > 0
        ? projectHoldMs
        : DEFAULT_PICTURE_HOLD_MS
  return Math.round(hold)
}

/**
 * Whether an element contributes anything to the film.
 *
 * An element with no media and no content is a placeholder someone added and
 * never filled — it must not become a beat of black in the middle of a film.
 */
export function elementIsRenderable(
  element: Pick<ProjectElement, 'kind' | 'clipIds' | 'contentMd' | 'itemElementIds'>,
  clipsById: ReadonlyMap<ClipId, ClipRecord>,
  /** The project's elements. Needed only to judge a `collage`. */
  elementsById?: ReadonlyMap<ElementId, ProjectElement>,
): boolean {
  if (element.kind === 'text') return Boolean(element.contentMd?.trim())
  if (element.kind === 'collage') {
    // A collage with nothing in it is the placeholder case: cells drawn from
    // items that are not there is a stretch of black with a layout.
    return collageItems(element, elementsById).some((item) =>
      elementIsRenderable(item, clipsById),
    )
  }
  return element.clipIds.some((id) => clipsById.has(id))
}

/**
 * The elements a collage is composed of, in reading order.
 *
 * Missing ids are dropped rather than left as holes, and so are nested collages
 * — the kind deliberately does not recurse (see `ProjectElement.itemElementIds`),
 * so anything that got one in there is a record to survive rather than a shape
 * to support.
 */
export function collageItems(
  element: Pick<ProjectElement, 'itemElementIds'>,
  elementsById: ReadonlyMap<ElementId, ProjectElement> | undefined,
): ProjectElement[] {
  if (!elementsById) return []
  const items: ProjectElement[] = []
  for (const id of element.itemElementIds ?? []) {
    const found = elementsById.get(id)
    if (found && found.kind !== 'collage') items.push(found)
  }
  return items
}

/* ── mutation helpers (pure) ─────────────────────────────────────────────── */

/** A fresh section, appended after the current last. */
export function newSection(
  projectId: string,
  name: string,
  existing: readonly ProjectSection[],
): ProjectSection {
  const trimmed = name.trim()
  return {
    id: newId('sec'),
    projectId,
    name: trimmed || `Section ${existing.length + 1}`,
    sort: existing.reduce((max, section) => Math.max(max, section.sort + 1), 0),
    createdAt: Date.now(),
  }
}

/** A fresh element, appended to the end of its section. */
export function newElement(input: {
  projectId: string
  sectionId: string
  kind: ElementKind
  clipIds?: ClipId[]
  contentMd?: string
  siblings: readonly ProjectElement[]
}): ProjectElement {
  const clipIds = input.clipIds ?? []
  const now = Date.now()
  return {
    id: newId('el'),
    projectId: input.projectId,
    sectionId: input.sectionId,
    kind: input.kind,
    sort: input.siblings.reduce((max, element) => Math.max(max, element.sort + 1), 0),
    clipIds,
    layout: defaultLayoutFor(clipIds.length),
    ...(input.contentMd ? { contentMd: input.contentMd } : {}),
    createdAt: now,
    updatedAt: now,
  }
}

/**
 * Put an ordered list back into dense 0..n-1 sorts.
 *
 * Dense rather than nudged, because sparse sorts drift into collisions and a
 * collision is an order that depends on which record happened to be read first.
 * Ties break by id so the result cannot depend on input order either.
 */
export function renumber<T extends { id: string; sort: number }>(items: readonly T[]): T[] {
  return [...items]
    .sort((a, b) => a.sort - b.sort || a.id.localeCompare(b.id))
    .map((item, index) => ({ ...item, sort: index }))
}

/**
 * Move an item to a new index within an ordered list.
 *
 * Shared by section and element reordering — the same operation on different
 * rows. An unknown id renumbers and changes nothing else, which is what a
 * reorder of something already deleted should do.
 */
export function reorder<T extends { id: string; sort: number }>(
  items: readonly T[],
  id: string,
  toIndex: number,
): T[] {
  const ordered = renumber(items)
  const from = ordered.findIndex((item) => item.id === id)
  if (from === -1) return ordered
  const clamped = Math.max(0, Math.min(toIndex, ordered.length - 1))
  const [lifted] = ordered.splice(from, 1)
  ordered.splice(clamped, 0, lifted!)
  return ordered.map((item, index) => ({ ...item, sort: index }))
}

/**
 * Move an element into another section, at the end of it.
 *
 * The source section's remaining elements are renumbered too: leaving a hole
 * would be harmless today and a collision the first time something assumes
 * dense sorts.
 */
export function moveElementToSection(
  elements: readonly ProjectElement[],
  elementId: ElementId,
  toSectionId: string,
): ProjectElement[] {
  const moving = elements.find((element) => element.id === elementId)
  if (!moving || moving.sectionId === toSectionId) return [...elements]

  const destination = elements.filter((element) => element.sectionId === toSectionId)
  const moved: ProjectElement = {
    ...moving,
    sectionId: toSectionId,
    sort: destination.reduce((max, element) => Math.max(max, element.sort + 1), 0),
    updatedAt: Date.now(),
  }

  const rest = elements.filter((element) => element.id !== elementId)
  const source = renumber(rest.filter((element) => element.sectionId === moving.sectionId))
  const untouched = rest.filter((element) => element.sectionId !== moving.sectionId)
  return [...untouched, ...source, moved]
}
